// Indicadores para los reportes de registros (Recepción de ropa sucia y Alistamientos),
// calculados sobre TODOS los registros del periodo (no solo la página visible de la tabla).

const pad = (n) => String(n).padStart(2, '0');

// Clave de día en hora LOCAL (no toISOString, que en Colombia corre el día después de las 7 p. m.)
const dayParts = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return {
    order: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    label: `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`,
  };
};

const round = (n, d = 2) => Number(Number(n || 0).toFixed(d));

const groupTop = (rows, keyFn, valueFns, sortKey, top = 10) => {
  const map = new Map();
  rows.forEach((r) => {
    const key = keyFn(r) || 'Sin dato';
    if (!map.has(key)) map.set(key, { nombre: key });
    const acc = map.get(key);
    Object.entries(valueFns).forEach(([k, fn]) => {
      acc[k] = (acc[k] || 0) + (Number(fn(r)) || 0);
    });
  });
  return Array.from(map.values())
    .map((x) => Object.fromEntries(Object.entries(x).map(([k, v]) => [k, typeof v === 'number' ? round(v) : v])))
    .sort((a, b) => b[sortKey] - a[sortKey])
    .slice(0, top);
};

// ─── Recepción de ropa sucia ─────────────────────────────────────────────────
export function computeRecepcionIndicadores(rows = []) {
  const lotes = rows.length;
  const kilos = rows.reduce((s, r) => s + (Number(r.pesoKg) || 0), 0);
  const finalizadosRows = rows.filter((r) => r.estado === 'finalizado');
  const kilosFinalizados = finalizadosRows.reduce((s, r) => s + (Number(r.pesoKg) || 0), 0);
  const kilosLimpios = finalizadosRows.reduce((s, r) => s + (Number(r.kilosLimpios) || 0), 0);
  const kilosRechazo = rows.reduce((s, r) => s + (Number(r.totalRechazoKilos) || 0), 0);
  // El rechazo solo se conoce al finalizar el lavado: se mide sobre los kilos finalizados
  const pctRechazo = kilosFinalizados > 0 ? (kilosRechazo / kilosFinalizados) * 100 : 0;

  const porDiaMap = new Map();
  rows.forEach((r) => {
    const p = dayParts(r.createdAt);
    if (!p) return;
    if (!porDiaMap.has(p.order)) porDiaMap.set(p.order, { fecha: p.label, order: p.order, lotes: 0, kilos: 0 });
    const acc = porDiaMap.get(p.order);
    acc.lotes += 1;
    acc.kilos += Number(r.pesoKg) || 0;
  });
  const porDia = Array.from(porDiaMap.values())
    .map((d) => ({ ...d, kilos: round(d.kilos, 1) }))
    .sort((a, b) => a.order.localeCompare(b.order));

  const rechazoMap = new Map();
  rows.forEach((r) => {
    (Array.isArray(r.rechazos) ? r.rechazos : []).forEach((x) => {
      const tipo = x?.tipo || 'otro';
      rechazoMap.set(tipo, (rechazoMap.get(tipo) || 0) + (Number(x?.kilos) || 0));
    });
  });
  const rechazoPorTipo = Array.from(rechazoMap.entries())
    .map(([tipo, kg]) => ({ nombre: tipo.charAt(0).toUpperCase() + tipo.slice(1).replace(/_/g, ' '), kilos: round(kg, 1) }))
    .sort((a, b) => b.kilos - a.kilos);

  return {
    kpis: {
      lotes,
      kilos,
      promKgLote: lotes > 0 ? kilos / lotes : 0,
      finalizados: finalizadosRows.length,
      pendientes: lotes - finalizadosRows.length,
      kilosFinalizados,
      kilosLimpios,
      kilosRechazo,
      pctRechazo,
      diasConDatos: porDia.length,
      kgPorDia: porDia.length > 0 ? kilos / porDia.length : 0,
    },
    porDia,
    porCliente: groupTop(rows, (r) => r.cliente?.nombre, { kilos: (r) => r.pesoKg, lotes: () => 1 }, 'kilos'),
    porTipoPrenda: groupTop(rows, (r) => r.tipoPrenda?.nombre, { kilos: (r) => r.pesoKg, lotes: () => 1 }, 'kilos'),
    porMaquina: groupTop(rows, (r) => r.maquina?.nombre, { kilos: (r) => r.pesoKg, lotes: () => 1 }, 'kilos'),
    rechazoPorTipo,
  };
}

// ─── Alistamientos ───────────────────────────────────────────────────────────
// pesoTotal y el peso de cada item vienen en GRAMOS (igual que AlistamientosReportesPage).
export function computeAlistamientosIndicadores(rows = []) {
  const total = rows.length;
  const prendas = rows.reduce((s, a) => s + (Number(a.totalItems) || 0), 0);
  const kilos = rows.reduce((s, a) => s + (Number(a.pesoTotal) || 0), 0) / 1000;
  const bolsas = rows.reduce((s, a) => s + (a.bolsas?.length || 0), 0);
  const entregados = rows.filter((a) => a.estado === 'Entregado').length;

  const porDiaMap = new Map();
  rows.forEach((a) => {
    const p = dayParts(a.fechaCreacion);
    if (!p) return;
    if (!porDiaMap.has(p.order)) {
      porDiaMap.set(p.order, { fecha: p.label, order: p.order, Lenceria: 0, Toallas: 0, kilos: 0 });
    }
    const acc = porDiaMap.get(p.order);
    if (a.zona === 'Lenceria' || a.zona === 'Toallas') acc[a.zona] += Number(a.totalItems) || 0;
    acc.kilos += (Number(a.pesoTotal) || 0) / 1000;
  });
  const porDia = Array.from(porDiaMap.values())
    .map((d) => ({ ...d, kilos: round(d.kilos, 1) }))
    .sort((a, b) => a.order.localeCompare(b.order));

  const estadoMap = new Map();
  rows.forEach((a) => estadoMap.set(a.estado || 'Sin estado', (estadoMap.get(a.estado || 'Sin estado') || 0) + 1));
  const porEstado = Array.from(estadoMap.entries())
    .map(([nombre, cantidad]) => ({ nombre, cantidad }))
    .sort((a, b) => b.cantidad - a.cantidad);

  const items = rows.flatMap((a) => (Array.isArray(a.items) ? a.items : []));
  const topPrendas = groupTop(
    items,
    (i) => (i.nombre || '').trim(),
    { cantidad: (i) => i.cantidad, kilos: (i) => ((Number(i.cantidad) || 0) * (Number(i.peso) || 0)) / 1000 },
    'cantidad',
  );

  return {
    kpis: {
      total,
      prendas,
      kilos,
      bolsas,
      promPrendas: total > 0 ? prendas / total : 0,
      promKg: total > 0 ? kilos / total : 0,
      entregados,
      pctEntregados: total > 0 ? (entregados / total) * 100 : 0,
      diasConDatos: porDia.length,
    },
    porDia,
    porEstado,
    porCliente: groupTop(rows, (a) => a.cliente?.nombre, { prendas: (a) => a.totalItems, kilos: (a) => (Number(a.pesoTotal) || 0) / 1000 }, 'prendas'),
    topPrendas,
    porZona: ['Lenceria', 'Toallas'].map((zona) => {
      const z = rows.filter((a) => a.zona === zona);
      return {
        nombre: zona,
        alistamientos: z.length,
        prendas: z.reduce((s, a) => s + (Number(a.totalItems) || 0), 0),
        kilos: round(z.reduce((s, a) => s + (Number(a.pesoTotal) || 0), 0) / 1000, 1),
      };
    }),
  };
}
